import { useCallback, useMemo, useState } from 'react';

import { createMultiFileProgressTracker, useOperationProgress } from './progressHelpers';
import { S3Service } from './s3Service';
import type { H2ODriveConfig, S3Error, S3Object } from './types';

export interface UseS3ServiceReturn {
  s3Service: S3Service | null;
  isConnected: boolean;
  isConnecting: boolean;
  currentWorkspace: string;
  objects: S3Object[];
  isLoading: boolean;
  error: S3Error | null;
  isUploading: boolean;
  uploadProgress: number;
  uploadError: S3Error | null;
  connect: (config: H2ODriveConfig) => Promise<boolean>;
  disconnect: () => void;
  listObjects: (prefix?: string) => Promise<void>;
  listAllObjects: (prefix?: string) => Promise<S3Object[]>;
  uploadFile: (file: File, key?: string, refreshPrefix?: string) => Promise<void>;
  uploadFiles: (files: FileList, refreshPrefix?: string) => Promise<void>;
  deleteFile: (key: string, refreshPrefix?: string) => Promise<void>;
  deleteDirectory: (directoryKey: string, refreshPrefix?: string) => Promise<void>;
  createDirectory: (directoryName: string, prefix?: string, refreshPrefix?: string) => Promise<void>;
  copyFile: (sourceKey: string, destinationKey: string, refreshPrefix?: string) => Promise<void>;
  copyDirectory: (sourceKey: string, destinationKey: string, refreshPrefix?: string) => Promise<void>;
  moveFile: (sourceKey: string, destinationKey: string, refreshPrefix?: string) => Promise<void>;
  moveDirectory: (sourceKey: string, destinationKey: string, refreshPrefix?: string) => Promise<void>;
  downloadFile: (key: string) => Promise<Blob>;
  cancelUpload: () => void;
  generatePresignedUrl: (key: string, expiresInSeconds?: number) => Promise<string>;
}

export const useS3Service = (): UseS3ServiceReturn => {
  const [s3Service, setS3Service] = useState<S3Service | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [currentWorkspace, setCurrentWorkspace] = useState('');
  const [currentPrefix, setCurrentPrefix] = useState<string | undefined>(undefined);
  const [objects, setObjects] = useState<S3Object[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<S3Error | null>(null);

  const uploadProgress = useOperationProgress();

  const connect = useCallback(async (config: H2ODriveConfig): Promise<boolean> => {
    try {
      setIsConnecting(true);

      const service = new S3Service();
      await service.initialize(config);

      if (service) {
        setS3Service(service);
        setIsConnected(true);
        setCurrentWorkspace(config.workspaceId);

        // Don't load objects automatically on connection
        // Let the consuming component decide when and what to load

        return true;
      } else {
        throw new Error('Failed to establish H2O Drive connection');
      }
    } catch (err) {
      console.error('Connection failed:', err);
      setIsConnected(false);
      return false;
    } finally {
      setIsConnecting(false);
    }
  }, []);

  const disconnect = useCallback(() => {
    setS3Service(null);
    setIsConnected(false);
    setCurrentWorkspace('');
  }, []);

  const listObjects = useCallback(
    async (prefix?: string): Promise<void> => {
      if (!s3Service) {
        throw new Error('S3 service not connected');
      }

      try {
        setIsLoading(true);
        setError(null);
        setCurrentPrefix(prefix);
        const result = await s3Service.listObjects(prefix);
        setObjects(result);
      } catch (err) {
        const s3Error = err as S3Error;
        setError(s3Error);
        throw s3Error;
      } finally {
        setIsLoading(false);
      }
    },
    [s3Service]
  );

  const listAllObjects = useCallback(
    async (prefix?: string): Promise<S3Object[]> => {
      if (!s3Service) {
        throw new Error('S3 service not connected');
      }

      try {
        return await s3Service.listAllObjects(prefix);
      } catch (err) {
        const s3Error = err as S3Error;
        setError(s3Error);
        throw s3Error;
      }
    },
    [s3Service]
  );

  const uploadFile = useCallback(
    async (file: File, key?: string, refreshPrefix?: string): Promise<void> => {
      if (!s3Service) {
        throw new Error('S3 service not connected');
      }

      try {
        uploadProgress.start();

        await s3Service.uploadFile(file, key, uploadProgress.updateProgress);

        await listObjects(refreshPrefix !== undefined ? refreshPrefix : currentPrefix);
      } catch (err) {
        const s3Error = err as S3Error;
        uploadProgress.setError(s3Error);
        throw s3Error;
      } finally {
        uploadProgress.reset();
      }
    },
    [s3Service, listObjects, currentPrefix, uploadProgress]
  );

  const uploadFiles = useCallback(
    async (files: FileList, refreshPrefix?: string): Promise<void> => {
      if (!s3Service) {
        throw new Error('S3 service not connected');
      }

      try {
        uploadProgress.start();

        const fileArray = Array.from(files);
        const progressTracker = createMultiFileProgressTracker(fileArray.length, uploadProgress.updateProgress);

        const uploadPromises = fileArray.map((file, index) =>
          s3Service.uploadFile(file, undefined, progressTracker.createFileProgressCallback(index))
        );

        await Promise.all(uploadPromises);
        await listObjects(refreshPrefix !== undefined ? refreshPrefix : currentPrefix);
      } catch (err) {
        const s3Error = err as S3Error;
        uploadProgress.setError(s3Error);
        throw s3Error;
      } finally {
        uploadProgress.reset();
      }
    },
    [s3Service, listObjects, currentPrefix, uploadProgress]
  );

  const deleteFile = useCallback(
    async (key: string, refreshPrefix?: string): Promise<void> => {
      if (!s3Service) {
        throw new Error('S3 service not connected');
      }

      try {
        uploadProgress.start();
        await s3Service.deleteFile(key);
        await listObjects(refreshPrefix !== undefined ? refreshPrefix : currentPrefix);
      } catch (err) {
        const s3Error = err as S3Error;
        uploadProgress.setError(s3Error);
        throw s3Error;
      } finally {
        uploadProgress.reset();
      }
    },
    [s3Service, listObjects, currentPrefix, uploadProgress]
  );

  const deleteDirectory = useCallback(
    async (directoryKey: string, refreshPrefix?: string): Promise<void> => {
      if (!s3Service) {
        throw new Error('S3 service not connected');
      }

      try {
        uploadProgress.start();
        await s3Service.deleteDirectory(directoryKey);
        await listObjects(refreshPrefix !== undefined ? refreshPrefix : currentPrefix);
      } catch (err) {
        const s3Error = err as S3Error;
        uploadProgress.setError(s3Error);
        throw s3Error;
      } finally {
        uploadProgress.reset();
      }
    },
    [s3Service, listObjects, currentPrefix, uploadProgress]
  );

  const createDirectory = useCallback(
    async (directoryName: string, prefix?: string, refreshPrefix?: string): Promise<void> => {
      if (!s3Service) {
        throw new Error('S3 service not connected');
      }

      try {
        uploadProgress.start();
        await s3Service.createDirectory(directoryName, prefix);
        await listObjects(refreshPrefix !== undefined ? refreshPrefix : currentPrefix);
      } catch (err) {
        const s3Error = err as S3Error;
        uploadProgress.setError(s3Error);
        throw s3Error;
      } finally {
        uploadProgress.reset();
      }
    },
    [s3Service, listObjects, currentPrefix, uploadProgress]
  );

  const copyFile = useCallback(
    async (sourceKey: string, destinationKey: string, refreshPrefix?: string): Promise<void> => {
      if (!s3Service) {
        throw new Error('S3 service not connected');
      }

      try {
        uploadProgress.start();
        await s3Service.copyFile(sourceKey, destinationKey);
        await listObjects(refreshPrefix !== undefined ? refreshPrefix : currentPrefix);
      } catch (err) {
        const s3Error = err as S3Error;
        uploadProgress.setError(s3Error);
        throw s3Error;
      } finally {
        uploadProgress.reset();
      }
    },
    [s3Service, listObjects, currentPrefix, uploadProgress]
  );

  const copyDirectory = useCallback(
    async (sourceKey: string, destinationKey: string, refreshPrefix?: string): Promise<void> => {
      if (!s3Service) {
        throw new Error('S3 service not connected');
      }

      try {
        uploadProgress.start();
        await s3Service.copyDirectory(sourceKey, destinationKey);
        await listObjects(refreshPrefix !== undefined ? refreshPrefix : currentPrefix);
      } catch (err) {
        const s3Error = err as S3Error;
        uploadProgress.setError(s3Error);
        throw s3Error;
      } finally {
        uploadProgress.reset();
      }
    },
    [s3Service, listObjects, currentPrefix, uploadProgress]
  );

  const moveFile = useCallback(
    async (sourceKey: string, destinationKey: string, refreshPrefix?: string): Promise<void> => {
      if (!s3Service) {
        throw new Error('S3 service not connected');
      }

      try {
        uploadProgress.start();
        await s3Service.moveFile(sourceKey, destinationKey);
        await listObjects(refreshPrefix !== undefined ? refreshPrefix : currentPrefix);
      } catch (err) {
        const s3Error = err as S3Error;
        uploadProgress.setError(s3Error);
        throw s3Error;
      } finally {
        uploadProgress.reset();
      }
    },
    [s3Service, listObjects, currentPrefix, uploadProgress]
  );

  const moveDirectory = useCallback(
    async (sourceKey: string, destinationKey: string, refreshPrefix?: string): Promise<void> => {
      if (!s3Service) {
        throw new Error('S3 service not connected');
      }

      try {
        uploadProgress.start();
        await s3Service.moveDirectory(sourceKey, destinationKey);
        await listObjects(refreshPrefix !== undefined ? refreshPrefix : currentPrefix);
      } catch (err) {
        const s3Error = err as S3Error;
        uploadProgress.setError(s3Error);
        throw s3Error;
      } finally {
        uploadProgress.reset();
      }
    },
    [s3Service, listObjects, currentPrefix, uploadProgress]
  );

  const downloadFile = useCallback(
    async (key: string): Promise<Blob> => {
      if (!s3Service) {
        throw new Error('S3 service not connected');
      }

      return await s3Service.downloadFile(key);
    },
    [s3Service]
  );

  const generatePresignedUrl = useCallback(
    async (key: string, expiresInSeconds = 3600): Promise<string> => {
      if (!s3Service) {
        throw new Error('S3 service not connected');
      }

      try {
        return await s3Service.generatePresignedUrl(key, expiresInSeconds);
      } catch (err) {
        throw err;
      }
    },
    [s3Service]
  );

  const memoizedS3Service = useMemo(() => s3Service, [s3Service]);

  return {
    s3Service: memoizedS3Service,
    isConnected,
    isConnecting,
    currentWorkspace,
    objects,
    isLoading,
    error,
    isUploading: uploadProgress.isInProgress,
    uploadProgress: uploadProgress.progress,
    uploadError: uploadProgress.error,
    connect,
    disconnect,
    listObjects,
    listAllObjects,
    uploadFile,
    uploadFiles,
    deleteFile,
    deleteDirectory,
    createDirectory,
    copyFile,
    copyDirectory,
    moveFile,
    moveDirectory,
    downloadFile,
    cancelUpload: uploadProgress.cancel,
    generatePresignedUrl,
  };
};
